import {
  useState,
  useContext,
  useEffect,
  lazy,
  Dispatch,
  SetStateAction,
} from 'react';
import { EstateSelector } from './EstateSelectorComponent';
import SearchField from './SearchFieldComponent';
import ExtraCriteriasFilter from './FilterComponents/ExtraCriteriasFilter';
import ExtraFilterBtn from './FilterComponents/ExtraFilterBtn';
import { FilterContext } from '../../filterTools';
import {
  EstateType,
  FilterTypes,
  RangeTagNames,
  ServicesTitles,
  Estates,
} from '../../Enums';
import useMediaQuery from '../CustomHooks/useMediaQuery';
import { Helmet, HelmetProvider } from 'react-helmet-async';
const CreateSearchProfileBtn = lazy(
  () => import('./FilterComponents/CreateSearchProfileBtn')
);

interface Props {
  setShowHideModal: Dispatch<SetStateAction<boolean>>;
  objectListLayout: string;
  handleShowLayout: (layout: string) => void;
}

export const FilterEstatesComponent = ({
  setShowHideModal,
  objectListLayout,
  handleShowLayout,
}: Props): JSX.Element => {
  const filterContext = useContext(FilterContext);
  const [tabIndex, setTabIndex] = useState<number | undefined>(-1);
  const [dataLayerClick, setDataLayerClick] = useState<boolean>(false);

  //<------------------States used for all dataLayers ----------------------->
  const [selectedCounties, setSelectedCounties] = useState<string[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [selectedMunicips, setSelectedMunicips] = useState<string[]>([]);
  const [selectedPolygons, setSelectedPolygons] = useState<string | null>(null);
  const [selectedEstateTypes, setSelectedEstateTypes] = useState<string[]>([]);
  const [selectedPriceMin, setSelectedPriceMin] = useState<string>();
  const [selectedPriceMax, setSelectedPriceMax] = useState<string>();
  const [selectedRoomMin, setSelectedRoomMin] = useState<string>();
  const [selectedRoomMax, setSelectedRoomMax] = useState<string>();
  const [selectedLivingSpaceMin, setSelectedLivingSpaceMin] =
    useState<string>();
  const [selectedLivingSpaceMax, setSelectedLivingSpaceMax] =
    useState<string>();
  const [selectedChargeMin, setSelectedChargeMin] = useState<string>();
  const [selectedChargeMax, setSelectedChargeMax] = useState<string>();
  const [selectedPlotMin, setSelectedPlotMin] = useState<string>();
  const [selectedPlotMax, setSelectedPlotMax] = useState<string>();
  const [selectedExtraCrits, setSelectedExtraCrits] = useState<string[]>([]);
  const [selectedAlignments, setSelectedAlignments] = useState<string[]>([]);
  const [selectedGreenEstate, setSelectedGreenEstate] = useState<
    boolean | undefined
  >(false);
  const [selectedHeadstart, setSelectedHeadstart] = useState<
    boolean | undefined
  >(false);
  const [selectedBidding, setSelectedBidding] = useState<boolean | undefined>(
    false
  );
  const [selectedSold, setSelectedSold] = useState<boolean | undefined>(false);
  const [showExtraCriterias, setShowExtraCriterias] = useState<boolean>(false);
  const [isScrollOverHeadline, setIsScrollOverHeadline] = useState(false);

  //<------------------States above used for all dataLayers ----------------------->
  useEffect(() => {
    setTabIndex(-1);
    setDataLayerClick(false);

    const allTagIds = filterContext.tags.map((y) => y.id);
    const allLabels = filterContext.tags.map((y) => y.label);
    const allTexts = filterContext.tags.map((y) => y.text);
    const allEstateTypes = filterContext.tags.map((x) => x.estateType);
    const priceTag = filterContext.tags.map(
      (x) => x.type === FilterTypes.RangeTag && x.name === RangeTagNames.Price
    );
    const roomTag = filterContext.tags.map(
      (x) => x.type === FilterTypes.RangeTag && x.name === RangeTagNames.Room
    );
    const livingSpageTag = filterContext.tags.map(
      (x) =>
        x.type === FilterTypes.RangeTag && x.name === RangeTagNames.LivingSpace
    );
    const chargeTag = filterContext.tags.map(
      (x) => x.type === FilterTypes.RangeTag && x.name === RangeTagNames.Charge
    );
    const plotAreaTag = filterContext.tags.map(
      (x) =>
        x.type === FilterTypes.RangeTag && x.name === RangeTagNames.PlotArea
    );
    const greenEstateTag = filterContext.tags.map(
      (x) =>
        x.type === FilterTypes.ShowGreenEstates &&
        x.label === ServicesTitles.GreenEstate &&
        x.isChecked
    );
    const headstartTag = filterContext.tags.map(
      (x) => x.type === FilterTypes.ShowOnlyHeadStart && x.isChecked
    );
    const soldTag = filterContext.tags.map(
      (x) => x.type === FilterTypes.ShowSold && x.isChecked
    );
    const polygonTag = filterContext.tags.map(
      (x) => x.type == FilterTypes.Polygons
    );

    let newSelectedCounties = [...selectedCounties];
    let newSelectedAreas = [...selectedAreas];
    let newSelectedMunicips = [...selectedMunicips];
    let newSelectedPolygons = selectedPolygons;
    let newSelectedEstateTypes = [...selectedEstateTypes];
    let newSelectedPriceMin = selectedPriceMin;
    let newSelectedPriceMax = selectedPriceMax;
    let newSelectedRoomMin = selectedRoomMin;
    let newSelectedRoomMax = selectedRoomMax;
    let newSelectedLivingSpaceMin = selectedLivingSpaceMin;
    let newSelectedLivingSpaceMax = selectedLivingSpaceMax;
    let newSelectedChargeMin = selectedChargeMin;
    let newSelectedChargeMax = selectedChargeMax;
    let newSelectedPlotMin = selectedPlotMin;
    let newSelectedPlotMax = selectedPlotMax;
    let newSelectedExtraCriterias = [...selectedExtraCrits];
    let newSelectedAlignments = [...selectedAlignments];
    let newSelectedGreenEstate = selectedGreenEstate;
    let newSelectedHeadstart = selectedHeadstart;
    let newSelectedBidding = selectedBidding;

    let newSelectedSold = selectedSold;

    filterContext.tags.forEach((x, i) => {
      if (
        x.type === FilterTypes.Area &&
        x.label &&
        !newSelectedAreas.includes(x.label)
      ) {
        newSelectedAreas = [...newSelectedAreas, x.label];
      }
      if (
        x.type === FilterTypes.County &&
        x.id &&
        !newSelectedCounties.includes(x.id)
      ) {
        newSelectedCounties = [...newSelectedCounties, x.id];
      }
      if (
        x.type === FilterTypes.Municipality &&
        x.id &&
        !newSelectedMunicips.includes(x.id)
      ) {
        newSelectedMunicips = [...newSelectedMunicips, x.id];
      }
      if (
        x.type === FilterTypes.Polygons &&
        x.polygons &&
        x.polygons.length > 0
      ) {
        //TODO POLYGONS should this be the value or the label??
        // newSelectedPolygons = x.label;
        newSelectedPolygons = x.polygons;
      }
      if (
        x.type === FilterTypes.EstateType &&
        x.estateType &&
        !newSelectedEstateTypes.includes(x.estateType)
      ) {
        newSelectedEstateTypes = [...selectedEstateTypes, x.estateType];
      }
      if (
        x.type === FilterTypes.RangeTag &&
        x.name === RangeTagNames.Price &&
        newSelectedPriceMin !== x.minVal.toString()
      ) {
        newSelectedPriceMin = x.minVal?.toString();
      }
      if (
        x.type === FilterTypes.RangeTag &&
        x.name === RangeTagNames.Price &&
        newSelectedPriceMax !== x.maxVal.toString()
      ) {
        newSelectedPriceMax = x.maxVal?.toString();
      }
      if (
        x.type === FilterTypes.RangeTag &&
        x.name === RangeTagNames.Room &&
        newSelectedRoomMin !== x.minVal.toString()
      ) {
        newSelectedRoomMin = x.minVal?.toString();
      }
      if (
        x.type === FilterTypes.RangeTag &&
        x.name === RangeTagNames.Room &&
        newSelectedRoomMax !== x.maxVal.toString()
      ) {
        newSelectedRoomMax = x.maxVal?.toString();
      }
      if (
        x.type === FilterTypes.RangeTag &&
        x.name === RangeTagNames.LivingSpace &&
        newSelectedLivingSpaceMin !== x.minVal.toString()
      ) {
        newSelectedLivingSpaceMin = x.minVal?.toString();
      }
      if (
        x.type === FilterTypes.RangeTag &&
        x.name === RangeTagNames.LivingSpace &&
        newSelectedLivingSpaceMax !== x.maxVal.toString()
      ) {
        newSelectedLivingSpaceMax = x.maxVal?.toString();
      }
      if (
        x.type === FilterTypes.RangeTag &&
        x.name === RangeTagNames.Charge &&
        newSelectedChargeMin !== x.minVal.toString()
      ) {
        newSelectedChargeMin = x.minVal?.toString();
      }
      if (
        x.type === FilterTypes.RangeTag &&
        x.name === RangeTagNames.Charge &&
        newSelectedChargeMax !== x.maxVal.toString()
      ) {
        newSelectedChargeMax = x.maxVal?.toString();
      }
      if (
        x.type === FilterTypes.RangeTag &&
        x.name === RangeTagNames.PlotArea &&
        newSelectedPlotMin !== x.minVal.toString()
      ) {
        newSelectedPlotMin = x.minVal?.toString();
      }
      if (
        x.type === FilterTypes.RangeTag &&
        x.name === RangeTagNames.PlotArea &&
        newSelectedPlotMax !== x.maxVal.toString()
      ) {
        newSelectedPlotMax = x.maxVal?.toString();
      }
      if (
        x.type === FilterTypes.ExtraCritera &&
        x.text &&
        !newSelectedExtraCriterias.includes(x.text)
      ) {
        newSelectedExtraCriterias = [...newSelectedExtraCriterias, x.text];
      }
      if (
        x.type === FilterTypes.Alignment &&
        x.id &&
        !newSelectedAlignments.includes(x.id)
      ) {
        newSelectedAlignments = [...newSelectedAlignments, x.id];
      }
      if (x.type === FilterTypes.ShowGreenEstates) {
        newSelectedGreenEstate = x.isChecked;
      }
      if (x.type === FilterTypes.ShowSold) {
        newSelectedSold = x.isChecked;
      }
      if (x.type === FilterTypes.ShowOnlyHeadStart) {
        newSelectedHeadstart = x.isChecked;
      }
      if (x.type === FilterTypes.ShowBidding) {
        newSelectedBidding = x.isChecked;
      }
    });

    newSelectedCounties = newSelectedCounties.filter((x) =>
      allTagIds.includes(x)
    );
    newSelectedAreas = newSelectedAreas.filter((x) => allLabels.includes(x));
    newSelectedMunicips = newSelectedMunicips.filter((x) =>
      allTagIds.includes(x)
    );
    if (!polygonTag.includes(true)) {
      newSelectedPolygons = undefined;
    }
    newSelectedEstateTypes = newSelectedEstateTypes.filter((x) =>
      allEstateTypes.includes(x as EstateType)
    );
    if (!priceTag.includes(true)) {
      newSelectedPriceMin = undefined;
      newSelectedPriceMax = undefined;
    }
    if (!roomTag.includes(true)) {
      newSelectedRoomMin = undefined;
      newSelectedRoomMax = undefined;
    }
    if (!livingSpageTag.includes(true)) {
      newSelectedLivingSpaceMin = undefined;
      newSelectedLivingSpaceMax = undefined;
    }
    if (!chargeTag.includes(true)) {
      newSelectedChargeMin = undefined;
      newSelectedChargeMax = undefined;
    }
    if (!plotAreaTag.includes(true)) {
      newSelectedPlotMin = undefined;
      newSelectedPlotMax = undefined;
    }
    newSelectedExtraCriterias = newSelectedExtraCriterias.filter((x) =>
      allTexts.includes(x)
    );
    newSelectedAlignments = newSelectedAlignments.filter((x) =>
      allTagIds.includes(x)
    );
    if (!greenEstateTag.includes(true)) {
      newSelectedGreenEstate = undefined;
    }
    if (!headstartTag.includes(true)) {
      newSelectedHeadstart = undefined;
    }
    if (!soldTag.includes(true)) {
      newSelectedSold = undefined;
    }
    //set initial state to new selected
    setSelectedCounties(newSelectedCounties);
    setSelectedAreas(newSelectedAreas);
    setSelectedMunicips(newSelectedMunicips);
    setSelectedPolygons(newSelectedPolygons);
    setSelectedEstateTypes(newSelectedEstateTypes);
    setSelectedPriceMin(newSelectedPriceMin);
    setSelectedPriceMax(newSelectedPriceMax);
    setSelectedRoomMin(newSelectedRoomMin);
    setSelectedRoomMax(newSelectedRoomMax);
    setSelectedLivingSpaceMin(newSelectedLivingSpaceMin);
    setSelectedLivingSpaceMax(newSelectedLivingSpaceMax);
    setSelectedChargeMin(newSelectedChargeMin);
    setSelectedChargeMax(newSelectedChargeMax);
    setSelectedPlotMin(newSelectedPlotMin);
    setSelectedPlotMax(newSelectedPlotMax);
    setSelectedExtraCrits(newSelectedExtraCriterias);
    setSelectedAlignments(newSelectedAlignments);
    setSelectedGreenEstate(newSelectedGreenEstate);
    setSelectedHeadstart(newSelectedHeadstart);
    setSelectedBidding(newSelectedBidding);

    setSelectedSold(newSelectedSold);
    let top = undefined;
    if (window.scrollY > 100 && filterContext.isBigScreen) {
      top = 0;
    } else if (filterContext.isSmallScreen && window.scrollY > 750) {
      top = 650;
    }
    if (top !== undefined) {
      window.scrollTo({
        top: top,
        behavior: 'smooth',
      });
    }
  }, [filterContext.tags]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY || window.pageYOffset;
      setIsScrollOverHeadline(scrollPosition > 64);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  //initial check if map is open
  const isSmallScreen = useMediaQuery('(max-width: 55.9rem)');
  const hash = window.location.hash.substring(1);
  const isMapInHash = hash.includes('map');

  return (
    <div
      className={`${
        isScrollOverHeadline
          ? 'lg:h-[calc(100vh-64px)]'
          : 'lg:h-[calc(100vh-124px)]'
      } bg-white lg:sticky lg:top-[64px] transition-all flex flex-col justify-between top-0 left-0 lg:w-full h-full lg:min-w-[25.8rem] lg:overflow-y-auto no-scrollbar lg:pt-3`}
    >
      <div className="bg-white lg:bg-none">
        {filterContext.isBigScreen && (
          <SearchField
            inputLabel={Estates.SearchPlaceholder}
            activeInput={false}
            countySelected={false}
            multipleEstateTypesSelected={false}
            brokerOrOfficeTypesSelected={false}
            commercialOrProject={false}
          />
        )}
        <div className="hidden lg:flex justify-center pt-4 pb-1">
          <button
            onClick={() => handleShowLayout('map')}
            className="flex gap-2 text-blue700"
          >
            <span className="whitespace-nowrap">Sök på karta</span>
            <img src="/Images/Icons/map-pin-line-blue.svg" alt="Sök på karta" />
          </button>
        </div>
        <EstateSelector />
        <ExtraCriteriasFilter
          tabIndex={tabIndex}
          showExtraCriterias={showExtraCriterias}
        />
        <ExtraFilterBtn
          tabIndex={tabIndex}
          setTabIndex={setTabIndex}
          showExtraCriterias={showExtraCriterias}
          setShowExtraCriterias={setShowExtraCriterias}
        />
      </div>

      {(!isSmallScreen || !isMapInHash) && (
        <CreateSearchProfileBtn
          sessionId={filterContext.sessionId}
          setShowHideModalFunction={setShowHideModal}
          isScrollOverHeadline={isScrollOverHeadline}
        />
      )}

      {dataLayerClick && (
        <HelmetProvider>
          <Helmet>
            <script>
              {`
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'filter_klick',
                    eventInfo: {
                      buttonText: ${Estates.SearchEstate}
                    }   
                });     
              `}
            </script>
          </Helmet>
        </HelmetProvider>
      )}
      {filterContext.tags && filterContext.tags.length !== 0 && (
        <HelmetProvider>
          <Helmet>
            <script>
              {`
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'filter',
                  eventInfo: {
                    län: ${
                      selectedCounties.length
                        ? `'${selectedCounties.sort()}'`
                        : undefined
                    },
                    kommun: ${
                      selectedMunicips.length
                        ? `'${selectedMunicips.sort()}'`
                        : undefined
                    },
                    område: ${
                      selectedAreas.length
                        ? `'${selectedAreas.sort()}'`
                        : undefined
                    },
                    boendetyp: ${
                      selectedEstateTypes.length
                        ? `'${selectedEstateTypes.sort()}'`
                        : undefined
                    },
                    pris: ${
                      selectedPriceMin !== undefined &&
                      selectedPriceMax !== undefined
                        ? `'${selectedPriceMin}-${selectedPriceMax}'`
                        : undefined
                    },
                    rum:  ${
                      selectedRoomMin !== undefined &&
                      selectedRoomMax !== undefined
                        ? `'${selectedRoomMin}-${selectedRoomMax}'`
                        : undefined
                    },
                    boarea:  ${
                      selectedLivingSpaceMin !== undefined &&
                      selectedLivingSpaceMax !== undefined
                        ? `'${selectedLivingSpaceMin}-${selectedLivingSpaceMax}'`
                        : undefined
                    },
                    avgift:  ${
                      selectedChargeMin !== undefined &&
                      selectedChargeMax !== undefined
                        ? `'${selectedChargeMin}-${selectedChargeMax}'`
                        : undefined
                    },
                    tomtarea:  ${
                      selectedPlotMin !== undefined &&
                      selectedPlotMax !== undefined
                        ? `'${selectedPlotMin}-${selectedPlotMax}'`
                        : undefined
                    },
                    nyckelord: ${
                      selectedExtraCrits.length
                        ? `'${selectedExtraCrits.sort()}'`
                        : undefined
                    },
                    inriktning: ${
                      selectedAlignments.length
                        ? `'${selectedAlignments.sort()}'`
                        : undefined
                    },
                    hållbar: ${
                      selectedGreenEstate !== undefined ? `'true'` : undefined
                    },
                    såld: ${selectedSold !== undefined ? `'true'` : undefined},
                    kommande: ${
                      selectedHeadstart !== undefined ? `'true'` : undefined
                    },
                    budgivning: ${
                      selectedBidding === true ? `'true'` : undefined
                    },
                    vy: ${`'${objectListLayout}'`}
                  }})
              `}
            </script>
          </Helmet>
        </HelmetProvider>
      )}
    </div>
  );
};
export default FilterEstatesComponent;
